/**
 * MelSearchResults Component
 * Full search results display with AI explanation and matches
 */

import React, { useState } from 'react';
import { cn } from '@/utils/cn';
import type { SmartSearchResponse, MelItem } from '@/types/mel';
import { MelDispatchStatus } from './MelDispatchStatus';
import { MelConditionsList } from './MelConditionsList';
import { MelItemCard, MelItemCardCompact } from './MelItemCard';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';

interface MelSearchResultsProps {
  results: SmartSearchResponse;
  onSelectItem?: (item: MelItem) => void;
  selectedItemId?: number;
  onIssueClick?: (item: MelItem) => void;
  className?: string;
}

export const MelSearchResults: React.FC<MelSearchResultsProps> = ({
  results,
  onSelectItem,
  selectedItemId,
  onIssueClick,
  className,
}) => {
  const [showAllMatches, setShowAllMatches] = useState(false);
  const explanation = results.aiExplanation;
  const matches = results.items;

  const hasExplanation = explanation !== null && explanation !== undefined;
  const displayedMatches = showAllMatches ? matches : matches.slice(0, 3);
  const hasMoreMatches = matches.length > 3;

  if (matches.length === 0) {
    return (
      <div className={cn('rounded-lg border border-border bg-card p-8 text-center', className)}>
        <ExclamationCircleIcon className="w-12 h-12 mx-auto text-foreground/30 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No MEL Items Found</h3>
        <p className="text-foreground/60 max-w-md mx-auto">
          No matching MEL items were found for your search. Try using different keywords or check
          the aircraft type.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Lookup Results Section */}
      {hasExplanation && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
                <MagnifyingGlassIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">MEL Lookup Result</h3>
                <p className="text-xs text-foreground/50">
                  Reference only — verify with maintenance before dispatch decisions
                </p>
              </div>
            </div>
            <MelDispatchStatus status={explanation.canDispatch} size="lg" />
          </div>

          {/* Summary */}
          <p className="text-foreground mb-4">{explanation.summary}</p>

          {/* Selected Item */}
          {explanation.selectedItem && (
            <div className="bg-background/50 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-medium text-foreground/60 mb-2">
                Closest MEL Entry
              </h4>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono font-bold text-primary">
                    {explanation.selectedItem.itemNumber}
                  </span>
                  <span className="mx-2 text-foreground/40">-</span>
                  <span className="font-medium text-foreground">
                    {explanation.selectedItem.title}
                  </span>
                </div>
                <div className="text-sm text-foreground/60">
                  Category {explanation.selectedItem.repairCategory}
                </div>
              </div>
            </div>
          )}

          {/* Conditions and Notes from MEL entry */}
          <MelConditionsList
            conditions={explanation.conditions}
            operationalNotes={explanation.operationalNotes}
            variant="card"
          />

          {/* Contact Maintenance + Issue MEL */}
          <div className="mt-4 pt-4 border-t border-blue-200/50 dark:border-blue-800/50">
            <div className="flex items-center gap-3 mb-3 text-sm text-foreground/70">
              <PhoneIcon className="w-4 h-4 flex-shrink-0" />
              <span>Contact your maintenance department to confirm this entry applies and review dispatch conditions.</span>
            </div>

            {onIssueClick && (
              <button
                onClick={() => {
                  const selectedMatch = matches.find(
                    (m) => m.item.itemNumber === explanation.selectedItem?.itemNumber
                  );
                  if (selectedMatch) {
                    onIssueClick(selectedMatch.item);
                  }
                }}
                className={cn(
                  'px-4 py-2 rounded-lg font-medium transition-colors',
                  'bg-primary text-primary-foreground hover:bg-primary-hover'
                )}
              >
                Issue MEL for This Item
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search Matches */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          {matches.length} Matching MEL Item{matches.length !== 1 ? 's' : ''}
        </h3>

        <div className="space-y-3">
          {displayedMatches.map((match) => (
            <MelItemCard
              key={match.item.id}
              item={match.item}
              score={match.score}
              matchType={match.matchType}
              isSelected={selectedItemId === match.item.id}
              onClick={onSelectItem ? () => onSelectItem(match.item) : undefined}
              showDetails={selectedItemId === match.item.id}
            />
          ))}
        </div>

        {/* Show More/Less Button */}
        {hasMoreMatches && (
          <button
            onClick={() => setShowAllMatches(!showAllMatches)}
            className={cn(
              'mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg',
              'text-sm font-medium text-foreground/60',
              'border border-border hover:bg-muted transition-colors'
            )}
          >
            {showAllMatches ? (
              <>
                <ChevronUpIcon className="w-4 h-4" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-4 h-4" />
                Show {matches.length - 3} More Result{matches.length - 3 !== 1 ? 's' : ''}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Compact version for sidebar or limited space
 */
export const MelSearchResultsCompact: React.FC<{
  results: SmartSearchResponse;
  onSelectItem?: (item: MelItem) => void;
  className?: string;
}> = ({ results, onSelectItem, className }) => {
  const explanation = results.aiExplanation;
  const matches = results.items;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Quick Status */}
      {explanation && (
        <div className="flex items-center justify-between">
          <MelDispatchStatus status={explanation.canDispatch} size="sm" />
          <span className="text-xs text-foreground/50">
            {matches.length} match{matches.length !== 1 ? 'es' : ''}
          </span>
        </div>
      )}

      {/* Compact Match List */}
      <div className="space-y-2">
        {matches.slice(0, 5).map((match) => (
          <MelItemCardCompact
            key={match.item.id}
            item={match.item}
            onClick={onSelectItem ? () => onSelectItem(match.item) : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default MelSearchResults;
